<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const REFETCH_DEBOUNCE_MS = 500;

const store = useStore();
const { t } = useI18n();

const followUpState = useMapGetter('conversationFollowUps/getState');
const uiFlags = useMapGetter('conversationFollowUps/getUIFlags');
const currentChat = useMapGetter('getSelectedChat');

// The module holds a single conversation's data; ignore it while it still
// belongs to the previously selected conversation.
const isCurrentConversation = computed(
  () => followUpState.value.conversationId === Number(props.conversationId)
);
const flow = computed(() =>
  isCurrentConversation.value ? followUpState.value.flow : null
);
const enrollment = computed(() =>
  isCurrentConversation.value ? followUpState.value.enrollment : null
);
const previewFirstSendAt = computed(() =>
  isCurrentConversation.value ? followUpState.value.previewFirstSendAt : null
);

const isError = computed(() => uiFlags.value.isError);
const isLoading = computed(
  () => uiFlags.value.isFetching || !isCurrentConversation.value
);
const isEnrolling = computed(
  () =>
    followUpState.value.enrollingConversationId === Number(props.conversationId)
);
const isCycleRunning = computed(() =>
  ['active', 'paused'].includes(enrollment.value?.status)
);

const statusLabel = computed(() => {
  switch (enrollment.value?.status) {
    case 'active':
      return t('CONVERSATION_SIDEBAR.FOLLOW_UP.STATUS.ACTIVE');
    case 'paused':
      return t('CONVERSATION_SIDEBAR.FOLLOW_UP.STATUS.PAUSED');
    case 'completed':
      return t('CONVERSATION_SIDEBAR.FOLLOW_UP.STATUS.COMPLETED');
    case 'cancelled':
      return t('CONVERSATION_SIDEBAR.FOLLOW_UP.STATUS.CANCELLED');
    case 'failed':
      return t('CONVERSATION_SIDEBAR.FOLLOW_UP.STATUS.FAILED');
    default:
      return '';
  }
});

const formatTime = value => {
  if (!value) return '';
  return new Date(value).toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fetchEnrollment = () => {
  store
    .dispatch('conversationFollowUps/fetch', Number(props.conversationId))
    .catch(() => {});
};

const isManaging = computed(() =>
  followUpState.value.managingConversationIds.includes(
    Number(props.conversationId)
  )
);

const manage = async (operation, successMessage) => {
  try {
    await store.dispatch('conversationFollowUps/manage', {
      conversationId: Number(props.conversationId),
      // Binds the operation to the cycle on screen: a stale panel conflicts
      // instead of managing a cycle the user never saw.
      enrollmentId: enrollment.value?.id,
      operation,
    });
    // Resuming a cycle whose cancellation cause accrued while paused ends it
    // instead — say so rather than announcing a resume that did not happen.
    if (
      operation === 'resume' &&
      followUpState.value.enrollment?.status === 'cancelled'
    ) {
      useAlert(t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.RESUME_CANCELLED'));
      return;
    }
    useAlert(successMessage);
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.ERROR');
    useAlert(message);
  }
};

const pauseCycle = () =>
  manage('pause', t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.PAUSE_SUCCESS'));
const resumeCycle = () =>
  manage('resume', t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.RESUME_SUCCESS'));

const removeDialogRef = ref(null);
const isRemoveDialogOpen = ref(false);
const requestRemove = () => {
  isRemoveDialogOpen.value = true;
  nextTick(() => removeDialogRef.value?.open());
};
const confirmRemove = () => {
  removeDialogRef.value?.close();
  isRemoveDialogOpen.value = false;
  manage('remove', t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.REMOVE_SUCCESS'));
};

const enroll = async () => {
  try {
    await store.dispatch(
      'conversationFollowUps/enroll',
      Number(props.conversationId)
    );
    useAlert(t('CONVERSATION_SIDEBAR.FOLLOW_UP.ENROLL.SUCCESS'));
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('CONVERSATION_SIDEBAR.FOLLOW_UP.ENROLL.ERROR');
    useAlert(message);
  }
};

// A cable event lost while offline self-heals here. RECONNECT fires when the
// attempt STARTS (HTTP may still be down — opportunistic try only);
// RECONNECT_COMPLETED fires after ReconnectService finished revalidating,
// which is the definitive refetch boundary.
onMounted(() => {
  fetchEnrollment();
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, fetchEnrollment);
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED, fetchEnrollment);
});
watch(() => props.conversationId, fetchEnrollment);

// Cycle-changing events land in the conversation the panel already renders
// (incoming reply, status change, assignment, a sent step's message):
// refetch when THIS conversation moves, debounced.
const conversationActivityKey = computed(() => {
  const chat = currentChat.value || {};
  if (Number(chat.id) !== Number(props.conversationId)) return null;
  const assignee = chat.meta?.assignee?.id ?? '';
  const team = chat.meta?.team?.id ?? '';
  return `${chat.messages?.length ?? 0}:${chat.status ?? ''}:${assignee}:${team}`;
});
let refetchTimer = null;
watch(conversationActivityKey, (next, previous) => {
  if (next === null || previous === null || next === previous) return;
  clearTimeout(refetchTimer);
  refetchTimer = setTimeout(fetchEnrollment, REFETCH_DEBOUNCE_MS);
});
onBeforeUnmount(() => {
  clearTimeout(refetchTimer);
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, fetchEnrollment);
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED, fetchEnrollment);
});
</script>

<template>
  <div class="flex flex-col gap-3 p-3 text-sm text-n-slate-12">
    <template v-if="isError">
      <p class="text-n-slate-11">
        {{ t('CONVERSATION_SIDEBAR.FOLLOW_UP.LOAD_ERROR') }}
      </p>
      <Button
        size="sm"
        variant="faded"
        color="slate"
        icon="i-lucide-refresh-cw"
        :label="t('CONVERSATION_SIDEBAR.FOLLOW_UP.RETRY')"
        class="self-start"
        @click="fetchEnrollment"
      />
    </template>

    <div v-else-if="isLoading" class="flex justify-center py-4">
      <Spinner class="size-5" />
    </div>

    <template v-else-if="isCycleRunning">
      <div class="flex items-center gap-2">
        <span
          class="text-xs font-medium px-2 py-0.5 rounded-full"
          :class="
            enrollment.status === 'active'
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-amber-3 text-n-amber-11'
          "
        >
          {{ statusLabel }}
        </span>
      </div>
      <p class="text-n-slate-11">
        {{
          t('CONVERSATION_SIDEBAR.FOLLOW_UP.FLOW_LABEL', {
            name: enrollment.flow_name,
          })
        }}
      </p>
      <p v-if="enrollment.next_step" class="text-n-slate-11">
        {{
          t('CONVERSATION_SIDEBAR.FOLLOW_UP.NEXT_STEP', {
            position: enrollment.next_step.follow_up_index + 1,
            total: enrollment.total_steps,
            time: formatTime(enrollment.next_step.scheduled_at),
          })
        }}
      </p>
      <p class="text-n-slate-11">
        {{
          t('CONVERSATION_SIDEBAR.FOLLOW_UP.SENT_COUNT', {
            count: enrollment.sent_steps,
            total: enrollment.total_steps,
          })
        }}
      </p>
      <div class="flex items-center gap-2 pt-1">
        <Button
          v-if="enrollment.status === 'active'"
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-pause"
          :label="t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.PAUSE')"
          :disabled="isManaging"
          @click="pauseCycle"
        />
        <Button
          v-else
          size="sm"
          variant="faded"
          color="teal"
          icon="i-lucide-play"
          :label="t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.RESUME')"
          :disabled="isManaging"
          @click="resumeCycle"
        />
        <Button
          size="sm"
          variant="faded"
          color="ruby"
          icon="i-lucide-trash-2"
          :label="t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.REMOVE')"
          :disabled="isManaging"
          @click="requestRemove"
        />
      </div>

      <Dialog
        v-if="isRemoveDialogOpen"
        ref="removeDialogRef"
        type="alert"
        :title="t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.REMOVE_CONFIRM.TITLE')"
        :description="
          t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.REMOVE_CONFIRM.DESCRIPTION')
        "
        :confirm-button-label="
          t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.REMOVE')
        "
        @confirm="confirmRemove"
        @close="isRemoveDialogOpen = false"
      />
    </template>

    <template v-else>
      <!-- The finished cycle's outcome renders even when the flow is gone
           (archiving cancels cycles and removes the active flow). -->
      <p v-if="enrollment" class="text-n-slate-11">
        {{ statusLabel }}
      </p>
      <p v-if="!flow" class="text-n-slate-11">
        {{ t('CONVERSATION_SIDEBAR.FOLLOW_UP.NO_FLOW') }}
      </p>
      <template v-else>
        <p class="text-n-slate-11">
          {{
            t('CONVERSATION_SIDEBAR.FOLLOW_UP.ENROLL.PREVIEW', {
              name: flow.name,
              time: formatTime(previewFirstSendAt),
            })
          }}
        </p>
        <Button
          size="sm"
          color="blue"
          icon="i-lucide-alarm-clock-plus"
          :label="t('CONVERSATION_SIDEBAR.FOLLOW_UP.ENROLL.BUTTON')"
          :is-loading="isEnrolling"
          :disabled="isEnrolling"
          class="self-start"
          @click="enroll"
        />
      </template>
    </template>
  </div>
</template>
